import React, { useCallback, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import classNames from 'classnames';
import {
  faArrowDown, faArrowUp, faBookmark, faBooks, faFileLines, faPen, faPlus, faSparkles, faTrashCan,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button } from 'components/ui/button';
import { Badge } from '@buildoutinc/blueprint-react/ui/Badge';
import { Combobox } from '@buildoutinc/blueprint-react/ui/Combobox';
import { Field } from '@buildoutinc/blueprint-react/ui/Field';
import { Input } from '@buildoutinc/blueprint-react/ui/Input';
import { List } from '@buildoutinc/blueprint-react/ui/List';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';
import { Textarea } from '@buildoutinc/blueprint-react/ui/Textarea';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

import type { DocumentReview, PlanPage, ReviewedPlan } from './use_document_composer';

// A plan page with a key that survives reordering. origin names the planned or addable page it came from,
// so "Add page" can offer it again once removed.
export interface ReviewPage extends PlanPage {
  key: string;
  origin?: string;
}

interface Candidate {
  origin: string;
  page: PlanPage;
}

// Base UI's grouped items shape; the new AI page's group has no label.
interface CandidateGroup {
  items: Candidate[];
  label: string | null;
}

const NEW_AI_PAGE_NAME = 'New AI page';
const AI_PAGE_ORIGIN = 'new-ai-page';
const AI_PAGE_OPTION: Candidate = {
  origin: AI_PAGE_ORIGIN,
  page: { type: 'ai_generated', name: NEW_AI_PAGE_NAME, prompt: '' },
};
// docs_pages.name is varchar(255).
const NAME_MAX_LENGTH = 255;

const PAGE_TYPES: Record<PlanPage['type'], { icon: IconDefinition; label: string }> = {
  ai_generated: { icon: faSparkles, label: 'AI' },
  library: { icon: faBooks, label: 'Library' },
  template_default: { icon: faFileLines, label: 'Template' },
  user_page: { icon: faBookmark, label: 'Saved page' },
};

// Groups the server's addable pages by the type they add; removed planned pages get their own group first.
const ADDABLE_GROUPS: { label: string; type: PlanPage['type'] }[] = [
  { label: 'This template', type: 'template_default' },
  { label: 'Page library', type: 'library' },
  { label: 'Saved pages', type: 'user_page' },
];

const planOrigin = (index: number) => `plan-${index}`;
const addableOrigin = (index: number) => `addable-${index}`;

let lastKey = 0;
const withKey = (page: PlanPage, origin?: string): ReviewPage => {
  lastKey += 1;
  return { ...page, key: `page-${lastKey}`, origin };
};

export const toReviewPages = (pages: PlanPage[]): ReviewPage[] => (
  pages.map((page, index) => withKey(page, planOrigin(index)))
);

export const toReviewedPlan = (pages: ReviewPage[], name: string | null): ReviewedPlan => ({
  name,
  pages: pages.map(({ key: _key, origin: _origin, ...page }) => (
    page.type === 'ai_generated'
      ? { ...page, name: page.name?.trim() || NEW_AI_PAGE_NAME, prompt: page.prompt?.trim() }
      : page
  )),
});

const displayName = (page: PlanPage) => (
  page.name?.trim() || (page.type === 'ai_generated' ? NEW_AI_PAGE_NAME : 'Untitled page')
);

const optionLabel = (option: Candidate) => (
  option.origin === AI_PAGE_ORIGIN ? 'AI-Generated Page' : displayName(option.page)
);

const isBlankAiPage = (page: PlanPage) => page.type === 'ai_generated' && !page.prompt?.trim();

// Stable, so React calls it once when the heading mounts rather than on every render.
const focusOnMount = (element: HTMLElement | null) => element?.focus();

interface Props {
  // A build failure, kept out of the polite region so its role="alert" is announced once.
  alert?: React.ReactNode;
  disabled?: boolean;
  // Build progress.
  notice?: React.ReactNode;
  pages: ReviewPage[];
  review: DocumentReview;
  onBack: () => void;
  onCreate: (plan: ReviewedPlan) => void;
  onPagesChange: (pages: ReviewPage[]) => void;
}

export function PlanReviewStep({
  alert,
  disabled = false,
  notice,
  pages,
  review,
  onBack,
  onCreate,
  onPagesChange,
}: Props) {
  const [editingKeys, setEditingKeys] = useState<string[]>([]);
  const [announcement, setAnnouncement] = useState('');
  // Removed pages as they were edited, by origin, so offering one again keeps its edits.
  const [removedPages, setRemovedPages] = useState<Record<string, PlanPage>>({});
  // Controls by `${page.key}-${role}`, so focus can follow a page after its row moves or remounts.
  const controls = useRef(new Map<string, HTMLElement>());
  // The page just added, focused once the menu it was added from has closed.
  const addedTarget = useRef<HTMLElement | null>(null);
  // Portaled into the dialog so the menu stacks above it; on <body> it falls under the modal's z-index.
  const [menuContainer, setMenuContainer] = useState<HTMLElement | undefined>();
  const findMenuContainer = useCallback((element: HTMLElement | null) => {
    setMenuContainer(element?.closest<HTMLElement>('.modal-content') ?? undefined);
  }, []);
  const [query, setQuery] = useState('');
  const search = query.trim().toLowerCase();
  const offered = (candidate: Candidate) => (
    !pages.some(page => page.origin === candidate.origin)
    && displayName(candidate.page).toLowerCase().includes(search)
  );
  const addable = review.addablePages.map((page, index) => ({ origin: addableOrigin(index), page }));
  // A new AI page stays first whatever the search, so it's there when nothing matches.
  const groups: CandidateGroup[] = [
    { items: [AI_PAGE_OPTION], label: null },
    {
      items: review.plan.pages.map((page, index) => {
        const origin = planOrigin(index);
        return { origin, page: removedPages[origin] ?? page };
      }).filter(offered),
      label: 'Removed pages',
    },
    ...ADDABLE_GROUPS.map(({ label, type }) => ({
      items: addable.filter(candidate => candidate.page.type === type && offered(candidate)),
      label,
    })),
  ].filter(group => group.label === null || group.items.length > 0);
  const incomplete = pages.length === 0 || pages.some(isBlankAiPage);

  const registerControl = (id: string) => (element: HTMLElement | null) => {
    if (element) {
      controls.current.set(id, element);
    } else {
      controls.current.delete(id);
    }
  };

  function focusFirst(ids: string[]) {
    const target = ids.map(id => controls.current.get(id))
      .find(element => element && !(element as HTMLButtonElement).disabled);
    target?.focus();
  }

  function update(key: string, changes: Partial<PlanPage>) {
    onPagesChange(pages.map(page => (page.key === key ? { ...page, ...changes } : page)));
  }

  function move(index: number, direction: 'up' | 'down') {
    const page = pages[index];
    const target = direction === 'up' ? index - 1 : index + 1;
    const next = [...pages];
    [next[index], next[target]] = [next[target], next[index]];

    flushSync(() => onPagesChange(next));
    focusFirst([`${page.key}-${direction}`, `${page.key}-${direction === 'up' ? 'down' : 'up'}`]);
    setAnnouncement(`${displayName(page)} moved to position ${target + 1} of ${next.length}`);
  }

  function remove(index: number) {
    const page = pages[index];
    const next = pages.filter(other => other.key !== page.key);
    const { key: _key, origin, ...values } = page;

    flushSync(() => {
      if (origin) setRemovedPages(current => ({ ...current, [origin]: values }));
      onPagesChange(next);
    });
    const neighbours = [next[index], next[index - 1]].filter(Boolean).map(other => `${other.key}-remove`);
    focusFirst([...neighbours, 'add']);
    setAnnouncement(`${displayName(page)} removed`);
  }

  function add(page: PlanPage, origin?: string) {
    const added = withKey(page, origin);
    flushSync(() => {
      if (page.type === 'ai_generated') setEditingKeys([...editingKeys, added.key]);
      onPagesChange([...pages, added]);
    });

    const target = controls.current.get(`${added.key}-prompt`) ?? controls.current.get(`${added.key}-row`);
    target?.scrollIntoView?.({ block: 'nearest' });
    addedTarget.current = target ?? null;
    setAnnouncement(`${displayName(page)} added`);
  }

  // The menu takes focus back while it closes, so the added page is focused only once it has.
  function handleMenuOpenChangeComplete(open: boolean) {
    if (open) {
      addedTarget.current = null;
    } else {
      setQuery('');
      addedTarget.current?.focus();
    }
  }

  function handleOptionSelected(option: Candidate | null) {
    if (option) add(option.page, option.origin === AI_PAGE_ORIGIN ? undefined : option.origin);
  }

  function toggleEditing(key: string) {
    setEditingKeys(editingKeys.includes(key) ? editingKeys.filter(other => other !== key) : [...editingKeys, key]);
  }

  function renderEditor(page: ReviewPage, blank: boolean) {
    const nameId = `${page.key}-name`;
    const promptId = `${page.key}-prompt`;
    const hintId = `${page.key}-prompt-hint`;

    return (
      <div className="d-flex flex-column gap-2" id={`${page.key}-editor`}>
        <Field>
          <Field.Label htmlFor={nameId}>Page name</Field.Label>
          <Input
            disabled={disabled}
            id={nameId}
            maxLength={NAME_MAX_LENGTH}
            placeholder={NEW_AI_PAGE_NAME}
            value={page.name ?? ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => update(page.key, { name: event.target.value })}
          />
        </Field>
        <Field>
          <Field.Label htmlFor={promptId}>Instructions for this page</Field.Label>
          <Textarea
            aria-describedby={blank ? hintId : undefined}
            aria-invalid={blank}
            disabled={disabled}
            id={promptId}
            ref={registerControl(promptId)}
            required
            rows={3}
            value={page.prompt ?? ''}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => (
              update(page.key, { prompt: event.target.value })
            )}
          />
          {blank && <Field.Description id={hintId}>Tell AI what this page should cover.</Field.Description>}
        </Field>
      </div>
    );
  }

  function pageSubtitle(page: ReviewPage) {
    if (page.type === 'ai_generated') return page.prompt;
    if (page.type === 'template_default') return `From ${review.template.name}`;
    if (page.type === 'library') return 'From your page library';

    return 'From your saved pages';
  }

  function renderPage(page: ReviewPage, index: number) {
    const { icon, label } = PAGE_TYPES[page.type];
    const name = displayName(page);
    const blank = isBlankAiPage(page);
    // A blank AI page stays open: its instructions are needed to create the document.
    const open = page.type === 'ai_generated' && (blank || editingKeys.includes(page.key));

    return (
      <List.Item
        className="flex-column align-items-stretch"
        key={page.key}
        ref={registerControl(`${page.key}-row`)}
        render={<li />}
        tabIndex={-1}
      >
        <div className="d-flex align-items-center gap-3">
          <span aria-hidden="true" className="text-muted small">{index + 1}</span>
          <FontAwesomeIcon className="text-accent" icon={icon} />
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <div className="fw-semibold text-truncate">{name}</div>
            {!open && <div className="text-muted fs-small text-truncate">{pageSubtitle(page)}</div>}
          </div>
          <Badge appearance={page.type === 'ai_generated' ? 'accent' : 'muted'} className="flex-shrink-0">
            {label}
          </Badge>
          <div className="d-flex flex-shrink-0 gap-1">
            {page.type === 'ai_generated' && (
              <Button
                aria-controls={`${page.key}-editor`}
                aria-expanded={open}
                aria-label={`Edit ${name}`}
                disabled={disabled || blank}
                icon={faPen}
                size="sm"
                tooltip={open ? 'Done editing' : 'Edit'}
                variant="ghost"
                onClick={() => toggleEditing(page.key)}
              />
            )}
            <Button
              aria-label={`Move ${name} up`}
              disabled={disabled || index === 0}
              icon={faArrowUp}
              ref={registerControl(`${page.key}-up`)}
              size="sm"
              tooltip="Move up"
              variant="ghost"
              onClick={() => move(index, 'up')}
            />
            <Button
              aria-label={`Move ${name} down`}
              disabled={disabled || index === pages.length - 1}
              icon={faArrowDown}
              ref={registerControl(`${page.key}-down`)}
              size="sm"
              tooltip="Move down"
              variant="ghost"
              onClick={() => move(index, 'down')}
            />
            <Button
              aria-label={`Remove ${name}`}
              disabled={disabled || pages.length === 1}
              icon={faTrashCan}
              ref={registerControl(`${page.key}-remove`)}
              size="sm"
              tooltip="Remove"
              variant="ghost"
              onClick={() => remove(index)}
            />
          </div>
        </div>
        {open && renderEditor(page, blank)}
      </List.Item>
    );
  }

  return (
    <>
      <Modal.Body className="d-flex flex-column gap-3" ref={findMenuContainer}>
        {alert}
        {/* Mounted while empty so screen readers announce what lands in it; hidden then so it takes no gap. */}
        <div aria-live="polite" className={classNames({ 'visually-hidden': !notice })}>
          {notice}
        </div>
        <div aria-live="polite" className="visually-hidden">{announcement}</div>
        <div>
          <h3 className="h6 mb-1" ref={focusOnMount} tabIndex={-1}>Review your document</h3>
          <div className="text-muted fs-small">
            Based on {review.template.name}. Reorder, remove or add pages, and tell AI what its pages should cover.
          </div>
        </div>

        <div className="d-flex flex-column gap-2">
          <div className="d-flex align-items-center gap-2">
            <span className="fw-semibold me-auto">{pages.length === 1 ? '1 page' : `${pages.length} pages`}</span>
            <Combobox
              disabled={disabled}
              filter={null}
              inputValue={query}
              items={groups}
              itemToStringLabel={optionLabel}
              value={null}
              onInputValueChange={setQuery}
              onOpenChangeComplete={handleMenuOpenChangeComplete}
              onValueChange={handleOptionSelected}
            >
              <Combobox.Trigger
                render={<Button icon={faPlus} ref={registerControl('add')} size="sm" variant="outline" />}
              >
                Add page
              </Combobox.Trigger>
              {/* Sized to its longest page name, and capped to the room left on screen so it never runs off it. */}
              <Combobox.Content
                align="end"
                className="d-flex flex-column gap-2"
                container={menuContainer}
                finalFocus={() => !addedTarget.current}
                style={{
                  maxHeight: 'min(var(--bp-combobox-viewport-max-height), var(--available-height))',
                  maxWidth: 'var(--available-width)',
                  minWidth: 'var(--anchor-width)',
                  width: 'max-content',
                }}
              >
                <Combobox.Input aria-label="Search pages" placeholder="Search pages" />
                <Combobox.List className="overflow-auto" style={{ minHeight: 0 }}>
                  {(group: CandidateGroup) => (
                    <Combobox.Group items={group.items} key={group.label ?? AI_PAGE_ORIGIN}>
                      {group.label && <Combobox.GroupLabel>{group.label}</Combobox.GroupLabel>}
                      <Combobox.Collection>
                        {(option: Candidate) => (
                          <Combobox.Item className="d-flex align-items-center gap-2" key={option.origin} value={option}>
                            <FontAwesomeIcon className="text-accent" icon={PAGE_TYPES[option.page.type].icon} />
                            <span className="text-truncate">{optionLabel(option)}</span>
                          </Combobox.Item>
                        )}
                      </Combobox.Collection>
                    </Combobox.Group>
                  )}
                </Combobox.List>
                {search && groups.length === 1 && <div className="text-muted fs-small px-2">No matching pages</div>}
              </Combobox.Content>
            </Combobox>
          </div>

          <List aria-label="Pages" className="overflow-auto" render={<ol />} style={{ maxHeight: '50vh' }}>
            {pages.map(renderPage)}
          </List>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button className="me-auto" disabled={disabled} label="Back" variant="secondary" onClick={onBack} />
        <Button
          disabled={disabled || incomplete}
          disabledTooltip={incomplete && !disabled ? 'Add instructions for every AI page' : undefined}
          label="Create document"
          onClick={() => onCreate(toReviewedPlan(pages, review.plan.name))}
        />
      </Modal.Footer>
    </>
  );
}
